import { useState } from 'react';
import { Check, ChevronRight, Globe } from 'lucide-react';
import { Popover, PopoverAnchor, PopoverContent } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/shared/hooks/useLanguage';

export interface SidebarLanguageMenuProps {
  onSelected?: () => void;
}

/**
 * The design opens the flyout on hover; the row is also a real button because a
 * hover-only submenu is unreachable by keyboard and by touch.
 */
export function SidebarLanguageMenu({ onSelected }: SidebarLanguageMenuProps) {
  const { t } = useTranslation('common');
  const { current, options, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);

  // Not awaited: the switch is optimistic and the write behind it is a network
  // round trip, so waiting would hold the menu open until the server answered.
  const choose = (code: string) => {
    void setLanguage(code);
    setOpen(false);
    onSelected?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
        {/* Anchor, not Trigger: Trigger toggles, and hover has already opened the
            flyout by the time the click lands, so it would shut it again. */}
        <PopoverAnchor asChild>
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
            className={`flex w-full cursor-pointer items-center gap-2.5 border-0 bg-transparent px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent ${open ? 'bg-accent' : ''}`}
          >
            <span className="flex size-md shrink-0 items-center justify-center text-muted-foreground">
              <Globe className="size-md" />
            </span>
            <span className="flex-1">{t('appShell.sidebarUserPopover.language')}</span>
            <ChevronRight className="size-md shrink-0 text-muted-foreground" />
          </button>
        </PopoverAnchor>

        <PopoverContent
          side="right"
          align="end"
          // Flush by necessity, not only by design: a gap belongs to neither the
          // row nor the flyout, so the pointer crossing it closes the menu.
          sideOffset={0}
          alignOffset={-8}
          role="menu"
          aria-label={t('appShell.sidebarUserPopover.languageMenuLabel')}
          className="min-w-60 p-1"
          // The flyout portals outside the hovered row, so it has to hold `open`
          // itself or the pointer entering it closes the list.
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          {options.map((locale) => (
            <button
              key={locale.code}
              type="button"
              role="menuitemradio"
              aria-checked={locale.code === current}
              onClick={() => choose(locale.code)}
              className="flex w-full cursor-pointer items-center gap-2.5 rounded-md border-0 bg-transparent px-3 py-2 text-left text-sm whitespace-nowrap text-foreground transition-colors hover:bg-accent"
            >
              <span className="flex-1">{locale.label}</span>
              {locale.code === current && <Check className="size-md shrink-0 text-foreground" />}
            </button>
          ))}
        </PopoverContent>
      </div>
    </Popover>
  );
}
